import { useEffect, useState } from 'react';
import type { Collector, LastSyncStatus } from '@/data/collectors';
import type { CredentialStatus } from '@/data/credentialStatus';
import { PageHeader } from '@/components/composites/PageHeader';
import { SectionCard } from '@/components/composites/SectionCard';
import { RotateCredentialModal } from '@/components/composites/RotateCredentialModal';
import { Badge } from '@/components/primitives/Badge';
import { Button } from '@/components/primitives/Button';
import { StatusDot } from '@/components/primitives/StatusDot';
import {
  useArchiveCollector,
  useCollectors,
  useSyncCollector,
  useUpdateCollector,
} from '@/hooks/useCollectors';
import {
  useCredentialStatus,
  useDeleteCredential,
  useTestCredential,
} from '@/hooks/useCredentialStatus';
import { useWorkerHeartbeat, type WorkerHealthy } from '@/hooks/useWorkerHeartbeat';
import { useSyncLogs } from '@/hooks/useSyncLogs';
import type { SyncLogRow } from '@/data/syncLogs';
import { useAppContext } from '@/context/AppContext';
import { FROZEN_NOW_MS } from '@/lib/frozenTime';
import { formatDateTimeShort } from '@/lib/dates';
import styles from './SettingsCollectorsPage.module.css';

const MINUTE_S = 60;
const HOUR_S = 60 * MINUTE_S;
const DAY_S = 24 * HOUR_S;

function relativePast(isoString: string): string {
  const thenMs = Date.parse(isoString);
  const diffS = Math.max(0, Math.round((FROZEN_NOW_MS - thenMs) / 1000));
  if (diffS < MINUTE_S) return `${diffS}s ago`;
  if (diffS < HOUR_S) return `${Math.round(diffS / MINUTE_S)}m ago`;
  if (diffS < DAY_S) return `${Math.round(diffS / HOUR_S)}h ago`;
  return `${Math.round(diffS / DAY_S)}d ago`;
}

function inFuture(seconds: number): string {
  if (seconds <= 0) return '\u2014';
  if (seconds < MINUTE_S) return `in ${seconds}s`;
  if (seconds < HOUR_S) return `in ${Math.round(seconds / MINUTE_S)}m`;
  return `in ${Math.round(seconds / HOUR_S)}h`;
}

function statusTone(status: Collector['status']): 'enabled' | 'warning' | 'disabled' {
  if (status === 'connected') return 'enabled';
  if (status === 'error') return 'warning';
  return 'disabled';
}

function collectorBorderClass(status: Collector['status']): string {
  if (status === 'connected') return styles.borderSuccess;
  if (status === 'error') return styles.borderError;
  return styles.borderDisabled;
}

function statusLabel(lastSyncStatus: LastSyncStatus): string {
  if (lastSyncStatus === 'success') return 'Success';
  if (lastSyncStatus === 'partial') return 'Partial';
  if (lastSyncStatus === 'failed') return 'Failed';
  return '\u2014';
}

function formatCount(value: number | null): string {
  return value === null ? '\u2014' : String(value);
}

/**
 * Sanitise a provider-side failure reason into one of a small closed set
 * of user-facing categories per SECURITY.md §PII-in-logs and
 * OBSERVABILITY.md §Redaction. Raw provider responses can carry PII
 * (emails, workspace names) so they never reach the UI verbatim. Full
 * redaction plus the sanitised-reason persistence lands with task 105c's
 * credential Test endpoint; this component only maps what the backend
 * already exposes.
 */
function sanitizedFailureReason(reason: string | null): string {
  if (!reason) return 'Unknown error';
  const lower = reason.toLowerCase();
  if (lower.includes('auth') || lower.includes('401') || lower.includes('403')) {
    return 'Authentication failed';
  }
  if (lower.includes('network') || lower.includes('timeout') || lower.includes('connect')) {
    return 'Network error';
  }
  if (lower.includes('rate') || lower.includes('429')) {
    return 'Rate limited';
  }
  return 'Unknown error';
}

type CredentialBadgeState = 'configured' | 'invalid' | 'not-configured';

function credentialBadgeState(status: CredentialStatus | null): CredentialBadgeState {
  if (!status || !status.exists) return 'not-configured';
  if (
    status.lastFailedAt &&
    (!status.lastUsedAt || Date.parse(status.lastFailedAt) > Date.parse(status.lastUsedAt))
  ) {
    return 'invalid';
  }
  return 'configured';
}

/* ── API Credential Card ── */

function ApiCredentialCard() {
  const { mockupMode } = useAppContext();
  const { data: status, isLoading } = useCredentialStatus();
  const testCredential = useTestCredential();
  const deleteCredential = useDeleteCredential();
  const [rotateOpen, setRotateOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState<{
    kind: 'info' | 'error' | 'success';
    text: string;
  } | null>(null);

  const state = credentialBadgeState(status);
  const exists = status?.exists ?? false;

  const badge =
    state === 'configured' ? (
      <Badge variant="enabled">Configured</Badge>
    ) : state === 'invalid' ? (
      <Badge variant="disabled">Invalid</Badge>
    ) : (
      <Badge variant="disabled">Not configured</Badge>
    );

  const subtext =
    isLoading && !status
      ? 'Loading\u2026'
      : state === 'not-configured'
        ? 'Set via CLI or use the Rotate button to add a credential.'
        : state === 'invalid'
          ? sanitizedFailureReason(status?.lastFailureReason ?? null)
          : status?.lastUsedAt
            ? `Last validated ${relativePast(status.lastUsedAt)}`
            : 'Not validated yet';

  const handleRemoveConfirm = async () => {
    setRemoveError(null);
    if (mockupMode) {
      setRemoveOpen(false);
      return;
    }
    try {
      await deleteCredential.mutateAsync();
      setRemoveOpen(false);
      setTestMessage(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.includes('CREDENTIAL_NOT_FOUND') || /no credential/i.test(msg)) {
        setRemoveError('No credential to remove.');
      } else {
        setRemoveError(msg);
      }
    }
  };

  const handleTest = async () => {
    setTestMessage(null);
    if (mockupMode) {
      // Matches the org-settings-save mockup pattern from PR #177 — the
      // write-action appears to succeed, no network call is made.
      setTestMessage({ kind: 'success', text: 'Credential valid (mockup mode).' });
      return;
    }
    try {
      const res = await testCredential.mutateAsync();
      if (res.ok) {
        setTestMessage({ kind: 'success', text: 'Credential valid.' });
      } else {
        setTestMessage({
          kind: 'error',
          text: `Credential test failed: ${res.reason ?? 'Unknown error'}`,
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      // The apiFetch wrapper surfaces backend error shapes as thrown
      // Errors; translate the two common backend codes into neutral
      // copy (per Product charter — avoid accusatory language).
      if (msg.includes('CREDENTIAL_NOT_FOUND') || /no credential/i.test(msg)) {
        setTestMessage({ kind: 'info', text: 'No credential set — use Rotate to add one.' });
      } else if (msg.includes('RATE_LIMITED') || /rate.?limited/i.test(msg)) {
        setTestMessage({ kind: 'info', text: 'Please wait a moment before retesting.' });
      } else {
        setTestMessage({ kind: 'error', text: `Credential test failed: ${msg}` });
      }
    }
  };

  return (
    <>
      <SectionCard title="API Credential" subtitle="Insightful API key">
        <div className={styles.credentialBody}>
          <div className={styles.credentialHeader}>
            <span className={styles.credentialSubtext}>{subtext}</span>
            {badge}
          </div>
          {testMessage && (
            <p
              className={
                testMessage.kind === 'error'
                  ? styles.credentialFeedbackError
                  : testMessage.kind === 'success'
                    ? styles.credentialFeedbackSuccess
                    : styles.credentialFeedbackInfo
              }
            >
              {testMessage.text}
            </p>
          )}
          <div className={styles.credentialActions}>
            <Button variant="secondary" size="sm" onClick={() => setRotateOpen(true)}>
              Rotate
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                void handleTest();
              }}
              disabled={testCredential.isPending}
            >
              {testCredential.isPending ? 'Testing\u2026' : 'Test'}
            </Button>
            {exists && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setRemoveError(null);
                  setRemoveOpen(true);
                }}
              >
                Remove credential
              </Button>
            )}
          </div>
        </div>
      </SectionCard>
      <RotateCredentialModal
        open={rotateOpen}
        onClose={() => setRotateOpen(false)}
        exists={exists}
        mockupMode={mockupMode}
      />
      {removeOpen && (
        <div className={styles.confirmOverlay} role="dialog" aria-modal="true">
          <div className={styles.confirmPanel}>
            <h3 className={styles.confirmTitle}>Remove credential?</h3>
            <p className={styles.confirmText}>
              This removes the stored API key. The Insightful collector will stop syncing until a
              new credential is set.
            </p>
            {removeError && <p className={styles.confirmError}>{removeError}</p>}
            <div className={styles.confirmActions}>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => setRemoveOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={() => {
                  void handleRemoveConfirm();
                }}
                disabled={deleteCredential.isPending}
              >
                {deleteCredential.isPending ? 'Removing…' : 'Remove'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ── Collector Card ── */

/**
 * Admin-only action row on the collector card (task 109 D3 + D4):
 *   - Sync now: enqueues a one-shot sync via the pg-boss queue; disabled
 *     when the collector is off, no credential exists, or a sync is
 *     already in flight. Inline status text tracks the transition from
 *     queued → syncing → last-synced by polling the collectors list.
 *   - Archive collector: sets `archivedAt`; the backend hides the row
 *     from the default list endpoint on the next invalidation so the
 *     card disappears from the page.
 */
function CollectorAdminActions({ collector }: { collector: Collector }) {
  const { currentUser, mockupMode } = useAppContext();
  const { data: credentialStatus } = useCredentialStatus();
  const syncCollector = useSyncCollector();
  const archiveCollector = useArchiveCollector();

  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  // Inline sync-status tracking: we hold the last sync start-time we saw
  // at trigger time so the poll-watcher can detect when a new sync_log
  // row lands and flip the label back to "Last sync: …".
  const [syncInFlight, setSyncInFlight] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{
    kind: 'info' | 'error' | 'success';
    text: string;
  } | null>(null);

  const isAdmin = currentUser.role === 'admin';
  if (!isAdmin) return null;

  const isOff = collector.status === 'disconnected';
  const credentialExists = credentialStatus?.exists ?? false;
  const syncDisabled =
    isOff || !credentialExists || syncCollector.isPending || syncInFlight || mockupMode;

  const handleSync = async () => {
    setSyncMessage(null);
    if (mockupMode) {
      setSyncMessage({ kind: 'success', text: 'Sync triggered (mockup mode).' });
      return;
    }
    try {
      await syncCollector.mutateAsync({ id: collector.id });
      setSyncInFlight(true);
      setSyncMessage({ kind: 'info', text: 'Sync queued…' });
      // A 60s watchdog: the list query's natural refresh cadence will
      // flip the card's lastSyncAt when the job completes. This timer
      // just clears the in-flight chip if the caller wanders off.
      window.setTimeout(() => {
        setSyncInFlight(false);
        setSyncMessage(null);
      }, 60_000);
    } catch (err) {
      setSyncInFlight(false);
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.includes('RATE_LIMITED') || /rate.?limited/i.test(msg)) {
        setSyncMessage({ kind: 'info', text: 'Please wait before triggering another sync.' });
      } else if (msg.includes('COLLECTOR_DISABLED') || msg.includes('COLLECTOR_ARCHIVED')) {
        setSyncMessage({
          kind: 'error',
          text: 'Collector is off; enable it before triggering a sync.',
        });
      } else if (msg.includes('CREDENTIAL_NOT_FOUND') || /no credential/i.test(msg)) {
        setSyncMessage({ kind: 'error', text: 'No credential set; add one before syncing.' });
      } else if (msg.includes('SYNC_IN_FLIGHT')) {
        setSyncMessage({ kind: 'info', text: 'A sync is already running.' });
      } else {
        setSyncMessage({ kind: 'error', text: `Sync failed: ${msg}` });
      }
    }
  };

  const handleArchiveConfirm = async () => {
    setArchiveError(null);
    if (mockupMode) {
      setArchiveOpen(false);
      return;
    }
    try {
      await archiveCollector.mutateAsync({ id: collector.id });
      setArchiveOpen(false);
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  return (
    <div className={styles.collectorActions}>
      {syncMessage && (
        <p
          className={
            syncMessage.kind === 'error'
              ? styles.credentialFeedbackError
              : syncMessage.kind === 'success'
                ? styles.credentialFeedbackSuccess
                : styles.credentialFeedbackInfo
          }
        >
          {syncMessage.text}
        </p>
      )}
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          void handleSync();
        }}
        disabled={syncDisabled}
      >
        {syncCollector.isPending ? 'Starting…' : syncInFlight ? 'Syncing…' : 'Sync now'}
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => {
          setArchiveError(null);
          setArchiveOpen(true);
        }}
      >
        Archive collector
      </Button>
      {archiveOpen && (
        <div className={styles.confirmOverlay} role="dialog" aria-modal="true">
          <div className={styles.confirmPanel}>
            <h3 className={styles.confirmTitle}>Archive collector?</h3>
            <p className={styles.confirmText}>
              {`Archive "${collector.name}"? The worker will stop syncing this collector and it will be hidden from the default list. History is preserved; you can restore it from the API.`}
            </p>
            {archiveError && <p className={styles.confirmError}>{archiveError}</p>}
            <div className={styles.confirmActions}>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => setArchiveOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={() => {
                  void handleArchiveConfirm();
                }}
                disabled={archiveCollector.isPending}
              >
                {archiveCollector.isPending ? 'Archiving…' : 'Archive'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CollectorCard({ collector }: { collector: Collector }) {
  const tone = statusTone(collector.status);
  const isDisabled = collector.status === 'disconnected';
  const hasSync = collector.lastSyncStatus !== null;

  const meta: Array<{ label: string; value: string; mono?: boolean; danger?: boolean }> =
    collector.status === 'error'
      ? [
          { label: 'Status', value: statusLabel(collector.lastSyncStatus) },
          { label: 'Error', value: 'Last sync failed', danger: true },
          { label: 'Last Sync', value: relativePast(collector.lastSyncAt) },
          { label: 'Next Retry', value: inFuture(collector.nextSyncInS) },
        ]
      : collector.status === 'disconnected'
        ? [
            { label: 'Status', value: '\u2014' },
            { label: 'Last Sync', value: hasSync ? relativePast(collector.lastSyncAt) : 'Never' },
            { label: 'Next Sync', value: '\u2014' },
            { label: '', value: '' },
          ]
        : [
            { label: 'Status', value: statusLabel(collector.lastSyncStatus) },
            {
              label: 'Fetched / Upserted',
              value: `${formatCount(collector.recordsFetched)} / ${formatCount(collector.recordsUpserted)}`,
              mono: true,
            },
            { label: 'Last Sync', value: relativePast(collector.lastSyncAt) },
            { label: 'Next Sync', value: inFuture(collector.nextSyncInS) },
          ];

  return (
    <div
      className={`${styles.collectorCard} ${collectorBorderClass(collector.status)}`}
      style={isDisabled ? { opacity: 0.7 } : undefined}
    >
      <div className={styles.collectorHeader}>
        <span className={styles.collectorName}>{collector.name}</span>
        <EnabledBadge collector={collector} />
      </div>
      <div className={styles.collectorMeta}>
        {meta.map((m, i) => (
          <div key={i}>
            {m.label && <div className={styles.metaLabel}>{m.label}</div>}
            {m.value && (
              <div
                className={`${styles.metaValue}${m.mono ? ` ${styles.mono}` : ''}`}
                style={m.danger ? { color: 'var(--color-danger-500)' } : undefined}
              >
                {m.label === 'Status' && tone === 'enabled' ? (
                  <span className={styles.statusInline}>
                    <StatusDot tone={tone} />
                    <Badge variant="enabled">{m.value}</Badge>
                  </span>
                ) : m.label === 'Status' && tone === 'warning' ? (
                  <Badge variant="disabled">{m.value}</Badge>
                ) : (
                  m.value
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <SyncLogSection collectorId={collector.id} />
      <CollectorAdminActions collector={collector} />
    </div>
  );
}

/* ── Enabled/Disabled badge with admin-only toggle ── */

function EnabledBadge({ collector }: { collector: Collector }) {
  const { currentUser, mockupMode } = useAppContext();
  const isAdmin = currentUser.role === 'admin';
  const isDisabled = collector.status === 'disconnected';
  const updateCollector = useUpdateCollector();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);

  const badge = (
    <Badge variant={isDisabled ? 'disabled' : 'enabled'}>
      {isDisabled ? 'Disabled' : 'Enabled'}
    </Badge>
  );

  if (!isAdmin) return badge;

  const handleConfirm = async () => {
    setToggleError(null);
    if (mockupMode) {
      // Mockup mode short-circuits the write (PR #177 precedent).
      setConfirmOpen(false);
      return;
    }
    try {
      await updateCollector.mutateAsync({ id: collector.id, enabled: isDisabled });
      setConfirmOpen(false);
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  return (
    <>
      <button
        type="button"
        className={styles.enabledToggleButton}
        onClick={() => setConfirmOpen(true)}
        aria-label={isDisabled ? 'Enable collector' : 'Disable collector'}
      >
        {badge}
      </button>
      {confirmOpen && (
        <div className={styles.confirmOverlay} role="dialog" aria-modal="true">
          <div className={styles.confirmPanel}>
            <h3 className={styles.confirmTitle}>
              {isDisabled ? 'Enable collector?' : 'Disable collector?'}
            </h3>
            <p className={styles.confirmText}>
              {isDisabled
                ? `Enable "${collector.name}"? The worker will resume syncs on the next tick.`
                : `Disable "${collector.name}"? The worker will stop syncing until re-enabled.`}
            </p>
            {toggleError && <p className={styles.confirmError}>{toggleError}</p>}
            <div className={styles.confirmActions}>
              <Button
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => setConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="button"
                onClick={() => {
                  void handleConfirm();
                }}
                disabled={updateCollector.isPending}
              >
                {updateCollector.isPending ? 'Saving\u2026' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ── Sync log collapsible section ── */

function formatDurationMs(ms: number | null): string {
  if (ms === null) return '\u2014';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms / 60_000)}m`;
}

// DESIGN-RULES Rule 22: date formatting lives in src/lib/dates.ts; the
// sync-log "Start" column uses the shared formatDateTimeShort helper
// (absolute timestamps read better than relative "3h ago" for a table
// of runs).

function SyncLogSection({ collectorId }: { collectorId: string }) {
  const [expanded, setExpanded] = useState(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const { pages, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error } = useSyncLogs({
    collectorId,
    enabled: expanded,
  });

  const rows: SyncLogRow[] = pages.flatMap((p) => p.syncLogs);

  return (
    <div className={styles.syncLogSection}>
      <button
        type="button"
        className={styles.syncLogToggle}
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        <span aria-hidden="true" className={styles.syncLogChevron}>
          {expanded ? '\u25BE' : '\u25B8'}
        </span>
        Sync log
      </button>
      {expanded && (
        <div className={styles.syncLogBody}>
          {isLoading && <p className={styles.syncLogHint}>Loading\u2026</p>}
          {error && <p className={styles.syncLogError}>Could not load sync history.</p>}
          {!isLoading && !error && rows.length === 0 && (
            <p className={styles.syncLogHint}>No sync history yet.</p>
          )}
          {rows.length > 0 && (
            <table className={styles.syncLogTable}>
              <thead>
                <tr>
                  <th>Start</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Fetched</th>
                  <th>Upserted</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <SyncLogRowView
                    key={row.id}
                    row={row}
                    expanded={expandedRowId === row.id}
                    onToggle={() => setExpandedRowId((cur) => (cur === row.id ? null : row.id))}
                  />
                ))}
              </tbody>
            </table>
          )}
          {hasNextPage && (
            <Button
              variant="secondary"
              size="sm"
              type="button"
              onClick={fetchNextPage}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? 'Loading\u2026' : 'Load more'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function SyncLogRowView({
  row,
  expanded,
  onToggle,
}: {
  row: SyncLogRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  const isFailed = row.status === 'failed';
  const statusBadgeVariant: 'enabled' | 'disabled' =
    row.status === 'success' ? 'enabled' : 'disabled';
  return (
    <>
      <tr
        className={isFailed ? styles.syncLogRowFailed : undefined}
        onClick={isFailed ? onToggle : undefined}
        style={isFailed ? { cursor: 'pointer' } : undefined}
      >
        <td>{formatDateTimeShort(row.startedAt)}</td>
        <td className={styles.mono}>{formatDurationMs(row.durationMs)}</td>
        <td>
          <Badge variant={statusBadgeVariant}>{statusLabel(row.status)}</Badge>
        </td>
        <td className={styles.mono}>{row.recordsFetched}</td>
        <td className={styles.mono}>{row.recordsUpserted}</td>
      </tr>
      {isFailed && expanded && (
        <tr className={styles.syncLogDetailRow}>
          <td colSpan={5} className={styles.syncLogDetail}>
            {row.errorReason ?? 'No error detail available.'}
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Worker Heartbeat Card ── */

const LOCAL_TICK_MS = 1_000;

/**
 * Keeps a local 1-second tick counter. React-Hooks-purity-safe: the
 * state is only mutated from inside the interval callback, never from a
 * render body or an effect top. Callers pass the current server-response
 * identity as `dataKey` via the component `key` prop to remount this
 * hook when a refetch lands — that's what resets the counter to 0 at
 * each poll.
 */
function useLiveTick(): number {
  const [tickOffsetMs, setTickOffsetMs] = useState(0);
  useEffect(() => {
    const handle = setInterval(() => {
      setTickOffsetMs((n) => n + LOCAL_TICK_MS);
    }, LOCAL_TICK_MS);
    return () => clearInterval(handle);
  }, []);
  return tickOffsetMs;
}

function formatAge(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < MINUTE_S) return `${seconds}s`;
  if (seconds < HOUR_S) return `${Math.round(seconds / MINUTE_S)}m`;
  if (seconds < DAY_S) return `${Math.round(seconds / HOUR_S)}h`;
  return `${Math.round(seconds / DAY_S)}d`;
}

function formatInterval(ms: number): string {
  const seconds = Math.max(1, Math.round(ms / 1000));
  if (seconds < MINUTE_S) return `${seconds}s`;
  const minutes = Math.round(seconds / MINUTE_S);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.round(minutes / 60)}h`;
}

function dotClassFor(healthy: WorkerHealthy): string {
  if (healthy === 'amber') return `${styles.pulseDot} ${styles.pulseDotAmber}`;
  if (healthy === 'red') return `${styles.pulseDot} ${styles.pulseDotRed}`;
  return styles.pulseDot;
}

function WorkerHeartbeatCard() {
  const { data, error } = useWorkerHeartbeat();
  // Re-key the inner card on each refetch so the tick counter naturally
  // resets to 0 without setState-inside-effect. The inner hook only
  // mutates state from the interval callback.
  const dataIdentity = data ? `${data.lastTickAt}|${data.ageMs}` : 'none';
  return <WorkerHeartbeatCardInner key={dataIdentity} data={data} error={error} />;
}

function WorkerHeartbeatCardInner({
  data,
  error,
}: {
  data: ReturnType<typeof useWorkerHeartbeat>['data'];
  error: ReturnType<typeof useWorkerHeartbeat>['error'];
}) {
  const tickOffsetMs = useLiveTick();

  // Error state: query failed outright. Mirror the worker-unreachable
  // signal that 105b's parent task calls out in Q5. Dot goes red; text is
  // a clear sentence rather than a partial UI.
  if (error) {
    return (
      <div className={styles.heartbeatCard}>
        <div className={styles.heartbeatTitle}>Worker Heartbeat</div>
        <div className={styles.heartbeatStatus}>
          <span className={`${styles.pulseDot} ${styles.pulseDotRed}`} />
          <span className={styles.heartbeatDetail}>Worker unreachable</span>
        </div>
      </div>
    );
  }

  const healthy: WorkerHealthy = data?.healthy ?? 'red';
  const dotClass = dotClassFor(healthy);
  const effectiveAge = data?.ageMs !== null && data?.ageMs !== undefined ? data.ageMs : null;
  const detail =
    effectiveAge === null
      ? 'No heartbeat yet'
      : `Last heartbeat ${formatAge(effectiveAge + tickOffsetMs)} ago`;
  const pollText = data?.pollIntervalMs ? formatInterval(data.pollIntervalMs) : '30s';

  return (
    <div className={styles.heartbeatCard}>
      <div className={styles.heartbeatTitle}>Worker Heartbeat</div>
      <div className={styles.heartbeatStatus}>
        <span className={dotClass} />
        <span className={styles.heartbeatDetail}>
          {detail} &middot; Poll interval every <strong>{pollText}</strong>
        </span>
      </div>
    </div>
  );
}

/* ── Page ── */

export function SettingsCollectorsPage() {
  const { data: collectors } = useCollectors();
  const { currentUser } = useAppContext();
  // Q9 (parent task 105): hide the API Credential card from members.
  // The backend status route is admin-only; UI-side hiding avoids the
  // 403 noise that would otherwise pollute React Query error logs.
  const canSeeCredentials = currentUser.role === 'admin';

  return (
    <>
      <PageHeader title="Collectors" />
      {canSeeCredentials && <ApiCredentialCard />}
      {collectors.map((c) => (
        <CollectorCard key={c.id} collector={c} />
      ))}
      <WorkerHeartbeatCard />
    </>
  );
}
